import { cn } from "@cashsync/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * CashSync mark: a coin ("$") wrapped by a synchronization cycle
 * (two opposing in/out arrows) suggesting the flow of money between accounts
 * kept in sync. It is a custom SVG (no third-party icons) so the identity is
 * distinctive for finance + sync.
 *
 * Theme-proof contrast (the gotcha that broke GymLog/bso/field-check/
 * meal-planner): the mark does NOT use loose accent/stroke-accent/fill-success
 * on faint surfaces —that makes it invisible in several themes—. Instead it is
 * drawn with `currentColor` on a SOLID `bg-accent` chip +
 * `text-accent-foreground`, a pair the theme engine guarantees contrast for in
 * LIGHT/DARK, the ~16-18 gallery themes, and any custom accent at runtime. The
 * wordmark uses `text-foreground` (guaranteed contrast).
 */
function LogoMark(props: { class?: string }) {
  return (
    <span
      class={cn(
        // Solid chip with the accent / accent-foreground pair matched by the theme engine.
        "inline-flex items-center justify-center rounded-[28%] bg-accent text-accent-foreground shadow-sm",
        props.class,
      )}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[70%] w-[70%]"
      >
        {/* Ciclo de sincronización: dos arcos con punta de flecha (dinero in/out) */}
        <path
          d="M7 12.5a10 10 0 0 1 16-2.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
        <path
          d="M23 6.5v4.2h-4.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
        <path
          d="M25 19.5a10 10 0 0 1-16 2.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
        <path
          d="M9 25.5v-4.2h4.2"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
        {/* Símbolo de moneda centrado: identidad financiera */}
        <path
          d="M16 9.5v13M13.4 12.7c0-1.5 1.3-2.4 2.9-2.4s2.9.8 2.9 2.2c0 3-5.8 1.9-5.8 5 0 1.5 1.3 2.4 2.9 2.4s2.9-.9 2.9-2.4"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          fill="none"
        />
      </svg>
    </span>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 shrink-0" />
          <span class="text-xl font-bold tracking-tight text-foreground">CashSync</span>
        </>
      ) : (
        <>
          <LogoMark class="mb-2 h-16 w-16 shrink-0 transition-transform duration-300 hover:scale-105 active:scale-95" />
          <h1 class="text-3xl font-bold tracking-tight text-foreground">CashSync</h1>
        </>
      )}
    </div>
  );
}
